import proj4 from 'proj4';

export const transformCoordinates = (coordinates, fromCRS, toCRS) => {
  if (fromCRS === toCRS) {
    return coordinates;
  }
  if (Array.isArray(coordinates[0])) {
    return coordinates.map(coord => transformCoordinates(coord, fromCRS, toCRS));
  }
  return proj4(fromCRS, toCRS, coordinates);
};

export const generateInsertXML = (features, layerName, layerCRS) => {
  const featuresXML = features.map((feature, index) => {
    const { geometry, properties, geometry_name } = feature;

    if (!properties.id) {
      properties.id = `feature-${Date.now()}-${index}`;
    }

    const propertiesXML = Object.entries(properties)
    .filter(([key, value]) =>
      value !== '' &&
      value !== null &&
      value !== undefined &&
      value !== false &&
      !(typeof value === 'string' && value.trim() === '')
    )
    .map(([key, value]) => `<${key}>${value}</${key}>`)
    .join('');
  

    const coordinates = wrapCoordinates(geometry.coordinates, geometry.type);

    return `<wfs:Insert>
        <${layerName} xmlns:${layerName}="http://www.openplans.org/geo">
            <${geometry_name}>
                <gml:${geometry.type} srsName="${layerCRS}">
                    ${coordinates}
                </gml:${geometry.type}>
            </${geometry_name}>
            ${propertiesXML}
        </${layerName}>
    </wfs:Insert>`;
  }).join("");

  return `<?xml version="1.0" encoding="UTF-8"?>
<wfs:Transaction service="WFS" version="1.0.0"
    xmlns:wfs="http://www.opengis.net/wfs"
    xmlns:gml="http://www.opengis.net/gml"
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-transaction.xsd">
    ${featuresXML}
</wfs:Transaction>`;
};

export const generateDeleteXML = (featureId, layerName) => {
  return `<?xml version="1.0" encoding="UTF-8"?>
<wfs:Transaction service="WFS" version="1.0.0"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-transaction.xsd">
  <wfs:Delete typeName="geo:${layerName}">
    <ogc:Filter>
      <ogc:FeatureId fid="${featureId}"/>
    </ogc:Filter>
  </wfs:Delete>
</wfs:Transaction>`;
};

export const generateUpdateAttributeXML = (featureId, layerName, newAttributes) => {
  const propertiesXML = Object.keys(newAttributes)
    .map(key => {
      return `<wfs:Property>
        <wfs:Name>${key}</wfs:Name>
        <wfs:Value>${newAttributes[key]}</wfs:Value>
      </wfs:Property>`;
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8"?>
<wfs:Transaction service="WFS" version="1.0.0"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-transaction.xsd">
  <wfs:Update typeName="${layerName}">
    ${propertiesXML}
    <ogc:Filter>
      <ogc:FeatureId fid="${featureId}"/>
    </ogc:Filter>
  </wfs:Update>
</wfs:Transaction>`;
};

export const generateUpdateGeometryXML = (featureId, typeName, geometry, layerCRS, geometry_name) => {
  const coordinates = wrapCoordinates(geometry.coordinates, geometry.type);

  return `<?xml version="1.0" encoding="UTF-8"?>
<wfs:Transaction service="WFS" version="1.0.0"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:gml="http://www.opengis.net/gml"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-transaction.xsd">
  <wfs:Update typeName="${typeName}">
    <wfs:Property>
      <wfs:Name>${geometry_name}</wfs:Name>
      <wfs:Value>
        <gml:${geometry.type} srsName="${layerCRS}">
          ${coordinates}
        </gml:${geometry.type}>
      </wfs:Value>
    </wfs:Property>
    <ogc:Filter>
      <ogc:FeatureId fid="${featureId}"/>
    </ogc:Filter>
  </wfs:Update>
</wfs:Transaction>`;
};

const wrapCoordinates = (coordinates, type) => {
  if (type === 'MultiPolygon') {
    return coordinates.map(polygon =>
      `<gml:polygonMember>` +
      `<gml:Polygon>` +
      `<gml:outerBoundaryIs>` +
      polygon.map(ring =>
        `<gml:LinearRing>` +
        `<gml:coordinates>` + ring.map(coord => coord.join(',')).join(' ') + `</gml:coordinates>` +
        `</gml:LinearRing>`
      ).join('') +
      `</gml:outerBoundaryIs>` +
      `</gml:Polygon>` +
      `</gml:polygonMember>`
    ).join('');
  } else if (type === 'Polygon') {
    return `<gml:outerBoundaryIs>` +
      `<gml:LinearRing>` +
      `<gml:coordinates>` + coordinates[0].map(coord => coord.join(',')).join(' ') + `</gml:coordinates>` +
      `</gml:LinearRing>` +
      `</gml:outerBoundaryIs>`;
  } else if (type === 'MultiLineString') {
    return coordinates.map(line =>
      `<gml:lineStringMember>` +
      `<gml:LineString>` +
      `<gml:coordinates>` + line.map(coord => coord.join(',')).join(' ') + `</gml:coordinates>` +
      `</gml:LineString>` +
      `</gml:lineStringMember>`
    ).join('');
  } else if (type === 'LineString') {
    return `<gml:coordinates>${coordinates.map(coord => coord.join(',')).join(' ')}</gml:coordinates>`;
  }
  else if (type === 'MultiPoint') {
    return coordinates.map(point =>
      `<gml:pointMember>` +
      `<gml:Point>` +
      `<gml:coordinates>` + point.join(',') + `</gml:coordinates>` +
      `</gml:Point>` +
      `</gml:pointMember>`
    ).join('');
  } else if (type === 'Point') {
    return `<gml:coordinates>${coordinates.join(',')}</gml:coordinates>`;
  }
  return '';
};